/**
 * Browser login mint — kept off `client.ts` so AuthContext can import it
 * without cycling through `useAccess()`.
 */
import { getCorrelationId } from '@/lib/logging';
import { getPendingPurchasePriceId } from '@/lib/purchase-handoff-client';

export interface CompleteLoginUser {
  userId: string;
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  isGuest?: boolean;
  roles: string[];
}

export interface CompleteLoginResponse {
  success: boolean;
  user?: CompleteLoginUser;
  error?: string;
  message?: string;
  redirectTo?: string;
}

/**
 * Finish popup, redirect, and email-link flows through one server login mint.
 * Body is `{ idToken }`, plus `priceId` when a `/purchase?priceId=…` returnTo
 * stashed one earlier in this tab (Firebase Hosting strips cookies other than
 * `__session` from Cloud Run, so the purchase intent travels in the body, not
 * a cookie — see ADR 0003).
 *
 * The priceId stays stashed after a successful mint: `/api/auth/session`
 * clears a not-invited cookie, so the return from Stripe Checkout (a full page
 * load) has to mint the session again. PurchaseSuccessPage and logout clear it.
 */
export async function completeLogin(idToken: string): Promise<Response> {
  const priceId = getPendingPurchasePriceId();
  return fetch('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Correlation-Id': getCorrelationId(),
    },
    credentials: 'include',
    body: JSON.stringify(priceId ? { idToken, priceId } : { idToken }),
  });
}
